import { useCallback, type DragEvent } from 'react';
import { log } from '@/logger/logger';
import { TrackTypeEnum } from '@/hooks/useAnalytics';
import { PANEL_WIDTH } from '@/components/Recipe/consts/ui';
import {
  getActiveMediaToCanvasDrag,
  getMediaToCanvasDragAnalyticsProps,
  getMediaToCanvasDragTrackEventName,
  beginMediaToCanvasDrag,
  getMediaToImportItemIndex,
  isAssetKindDraggableToCanvas,
  isMediaToCanvasDropOnDragSource,
  MEDIA_TO_CANVAS_DRAG_MIME,
  resolveMediaToCanvasDropMode,
  toMediaToCanvasDragScreenRect,
  MediaToCanvasDragMode,
  MediaToCanvasDragSource,
  getMediaDragPreviewDropScreenPosition,
  parseMediaToCanvasDragPayload,
  setMediaToCanvasDragPayload,
  setMediaToCanvasDragPreview,
  updateMediaToCanvasDragDropEffect,
} from '@/utils/mediaToCanvasDrag.utils';
import type { FlowView } from '@/components/Recipe/Views/FlowView';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { XYPosition } from 'reactflow';

export { MediaToCanvasDragMode, MediaToCanvasDragSource };

const logger = log.getLogger('useMediaToCanvasDrag');

type TrackMediaToCanvasDrop = (
  eventName: string,
  properties: ReturnType<typeof getMediaToCanvasDragAnalyticsProps> & { origin: 'full_screen' | 'node' },
  trackType: TrackTypeEnum,
) => void;

type UseMediaToCanvasDragArgs = {
  source: MediaToCanvasDragSource;
  sourceNodeId: string;
  enabled: boolean;
  getAssetKindAtIndex?: (index: number) => string | undefined;
  getPreviewUrlAtIndex?: (index: number) => string | null;
  /**
   * Dragging out of the full screen gallery: always a copy (the item stays in its node), and no source
   * tile rect, since the gallery closes and its thumbnail's screen area would otherwise reject drops.
   */
  fromFullScreen?: boolean;
};

type UseMediaToCanvasDropArgs = {
  enabled: boolean;
  isEditor: boolean;
  flowView: FlowView;
  nodeTypes: NodeDefinition[];
  saveWorkflow: () => Promise<unknown> | void;
  screenToFlowPosition: (position: { x: number; y: number }) => XYPosition;
  track: TrackMediaToCanvasDrop;
};

export function useMediaToCanvasDrag(args: UseMediaToCanvasDragArgs) {
  const { enabled, fromFullScreen = false, getAssetKindAtIndex, getPreviewUrlAtIndex, source, sourceNodeId } = args;
  const isMediaToCanvasDragAllowed = Boolean(enabled);

  const onGridDragStart = useCallback(
    (event: DragEvent, index: number) => {
      if (!isMediaToCanvasDragAllowed) return;
      const assetKind = getAssetKindAtIndex?.(index);
      if (assetKind !== undefined && !isAssetKindDraggableToCanvas(assetKind)) {
        event.preventDefault();
        return;
      }

      event.stopPropagation();

      const isDuplicate = fromFullScreen || event.altKey;
      const mode = isDuplicate ? MediaToCanvasDragMode.Duplicate : MediaToCanvasDragMode.Move;
      const tileRoot = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
      beginMediaToCanvasDrag({
        sourceNodeId,
        duplicateModifier: isDuplicate,
        sourceTileRect:
          tileRoot && !fromFullScreen ? toMediaToCanvasDragScreenRect(tileRoot.getBoundingClientRect()) : null,
        fromFullScreen,
      });
      const { offsetX: previewOffsetX, offsetY: previewOffsetY } = setMediaToCanvasDragPreview(
        event,
        getPreviewUrlAtIndex?.(index) ?? null,
        assetKind,
      );
      const payload =
        source === MediaToCanvasDragSource.AiModel
          ? { source, sourceNodeId, generationIndex: index, mode, previewOffsetX, previewOffsetY }
          : { source, sourceNodeId, fileIndex: index, mode, previewOffsetX, previewOffsetY };
      setMediaToCanvasDragPayload(event, payload);
    },
    [fromFullScreen, getAssetKindAtIndex, getPreviewUrlAtIndex, isMediaToCanvasDragAllowed, source, sourceNodeId],
  );

  return { isMediaToCanvasDragAllowed, onGridDragStart };
}

export function useMediaToCanvasDrop(args: UseMediaToCanvasDropArgs) {
  const { enabled, flowView, isEditor, nodeTypes, saveWorkflow, screenToFlowPosition, track } = args;

  const onMediaToCanvasDragOver = useCallback(
    (event: DragEvent) => {
      if (!enabled) {
        return;
      }
      updateMediaToCanvasDragDropEffect(event);
    },
    [enabled],
  );

  const onMediaToCanvasDrop = useCallback(
    (event: DragEvent) => {
      if (!isEditor || !enabled) {
        return false;
      }

      const rawPayload = event.dataTransfer.getData(MEDIA_TO_CANVAS_DRAG_MIME);
      if (!rawPayload) {
        return false;
      }

      const payload = parseMediaToCanvasDragPayload(rawPayload);
      if (!payload || event.clientX < PANEL_WIDTH) {
        return false;
      }

      if (isMediaToCanvasDropOnDragSource(event.clientX, event.clientY, payload.sourceNodeId)) {
        return true;
      }

      const mode = resolveMediaToCanvasDropMode(event.altKey);
      const dropScreen = getMediaDragPreviewDropScreenPosition(event.clientX, event.clientY, payload);
      const flowPosition = screenToFlowPosition(dropScreen);
      const newNode = flowView.addImportFromMediaSourceAtFlowPosition(payload.sourceNodeId, {
        sourceKind: payload.source,
        mode,
        itemIndex: getMediaToImportItemIndex(payload),
        flowPosition,
        nodeTypes,
      });

      if (newNode) {
        track(
          getMediaToCanvasDragTrackEventName(mode),
          {
            ...getMediaToCanvasDragAnalyticsProps(payload),
            origin: getActiveMediaToCanvasDrag()?.fromFullScreen ? 'full_screen' : 'node',
          },
          TrackTypeEnum.Product,
        );
        // This path edits FlowView directly instead of the Flow addNewNode callback, so persist explicitly.
        void Promise.resolve(saveWorkflow()).catch((err) => {
          logger.error('Failed to save workflow after canvas media drag', err);
        });
      } else {
        logger.warn('Canvas node media drag did not create a node', {
          source: payload.source,
          sourceNodeId: payload.sourceNodeId,
          mode,
        });
      }

      return true;
    },
    [enabled, flowView, isEditor, nodeTypes, saveWorkflow, screenToFlowPosition, track],
  );

  return { onMediaToCanvasDragOver, onMediaToCanvasDrop };
}
